import { ChangeEvent } from "react";
import { useFormikContext } from "formik";
import { LabeledInput } from "../../../Components/ConfigurationTab/LabeledInput";
import { UnitAdornment } from "../../../Components/ConfigurationTab/UnitAdornment";
import { PRICE_UNIT } from "../../../price";
import { SETTINGS_LABEL_PLACEMENT } from "./constants";

const non_negative_number_regex = (decimals: number) =>
  "[0-9]+[,.]?[0-9]{0," + decimals + "}";
export const price_regex = "-?" + non_negative_number_regex(6);
export const quantity_regex = non_negative_number_regex(1);

export const formatPriceInput = (raw: string): string =>
  raw
    .replace(",", ".")
    .replace(/[^0-9./-]/g, "")
    // Only a bare leading point gets a zero: ".1" -> "0.1", "-.1" -> "-0.1".
    // Padding on the value instead (0 < |n| < 1) turned a typed "0.1" into
    // "00.1".
    .replace(/^(-?)\./, (_, sign: string) => sign + "0.");

/// Format a onchange event for an input field
/// Converts "," to "." and pads the number with a "0" if is
export type FormValues = { [key: string]: undefined | number };
export const PriceField = <T extends string>(props: {
  label: string;
  name: T;
  /** Hint inside the empty input; defaults to the label. Pass a shorter one
   * where a long label would be clipped beside the unit. */
  placeholder?: string;
  required?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Defaults to the settings row's caption on top. */
  labelPlacement?: "start" | "top";
  /** See `LabeledInput`; for lining up a column of side-labelled fields. */
  labelWidth?: number | string;
}) => {
  const { values, errors, setFieldValue } =
    useFormikContext<{ [K in T]: number | undefined }>();

  const format_price = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFieldValue(e.target.name, formatPriceInput(e.target.value));
  };
  return (
    <LabeledInput
      required={props.required ?? true}
      onChange={format_price}
      label={props.label}
      placeholder={props.placeholder}
      labelPlacement={props.labelPlacement ?? SETTINGS_LABEL_PLACEMENT}
      labelWidth={props.labelWidth}
      error={Boolean(errors[props.name])}
      type="text"
      name={props.name}
      value={values[props.name] ?? ""}
      endAdornment={<UnitAdornment unit={PRICE_UNIT} />}
      inputRef={props.inputRef}
      inputProps={{
        pattern: price_regex,
        step: "10",
      }}
      autoComplete="off"
    />
  );
};
